var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { Subject, Observable, of, fromEvent, merge, interval, forkJoin } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { Store, Actions, ofActionDispatched, Select, ofActionCompleted } from '@ngxs/store';
import { map, filter, take, takeUntil, tap, skipUntil, finalize } from 'rxjs/operators';
import { SetupWidget, AuthWidgetFailure, AuthWidget, LogWidgetInteraction } from 'src/app/widget-auth/state/widget.actions';
import { WidgetInteractionEnum } from 'src/app/widget-auth/models/widget-interaction.enum';
import { SelectNode, ToggleVisibleNodes, HideInverse, TogglePickableNodes, ToggleInversePickableNodes, Isolate } from 'src/app/engine/state/nodes/engine.nodes.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { WidgetAPIHandler } from 'src/app/widget-api/api-handler';
import { ViewState } from 'src/app/view/state/view.state';
import { ViewActions } from 'src/app/view/state/view.actions';
import { WidgetWiring } from 'src/app/shared/widget-wiring';
import { LoadStoryFail, SetIncomingStory, LoadStoryChunkSuccess } from 'src/app/story/state/story.actions';
import { WidgetErrorTypeEnum } from '../../models/widget-error-type.enum';
import { ErrorService } from 'src/app/shared/error.service';
import { EngineService } from 'src/app/engine/engine.service';
import { WidgetState } from 'src/app/widget-auth/state/widget.state';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { SetChapters, SetAllChaptersBackground } from 'src/app/story/state/chapters/story.chapters.actions';
import { CameraService } from 'src/app/engine/camera.service';
import { animationFrame } from 'rxjs/internal/scheduler/animationFrame';
import { SetCamera } from 'src/app/engine/state/camera/engine.camera.actions';
import * as Hammer from 'hammerjs';
import { get, isNil } from 'lodash';
import { LocationStrategy } from '@angular/common';
import { SetScrollMode } from 'src/app/engine/state/engine.actions';
import { BackgroundModel } from 'src/app/engine/models/background.model';
export class WidgetMasterComponent {
    constructor(locationStrategy, router, store, actions, activatedRoute, widgetAPIHandler, errorService, engineService, cameraService, renderer) {
        this.locationStrategy = locationStrategy;
        this.router = router;
        this.store = store;
        this.actions = actions;
        this.activatedRoute = activatedRoute;
        this.widgetAPIHandler = widgetAPIHandler;
        this.errorService = errorService;
        this.engineService = engineService;
        this.cameraService = cameraService;
        this.renderer = renderer;
        this.title = 'Biodigital Human';
        this.ngUnsubscribe = new Subject();
    }
    ngOnInit() {
        // begin error tracking
        this.initErrorHandling();
        // begin interaction tracking
        this.initInteractions();
        const widgetConfig = this.activatedRoute.snapshot.data.widgetConfig;
        this.initWidgetBreakpoints(widgetConfig);
        this.initWidgetSetup(widgetConfig);
        this.initWidgetAuth();
        this.initViewAnalytics();
        this.initInteractionAnalytics();
        // init model default states
        this.initBackgroundFromWidget(widgetConfig);
        this.initBackgroundFromChapter();
        this.initCamera(widgetConfig);
        this.initRotation(widgetConfig);
        // start listening for API requests
        this.widgetAPIHandler.listen();
        // This is a more accurate implementation of 'human.ready'
        this.store
            .select(StoryChaptersState.getAllChaptersModelsReady)
            .pipe(filter(event => event === true), takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.engineService.fireEvent(EventsEnum.Started, null);
        });
    }
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    // setup widget config
    initWidgetSetup(widgetConfig) {
        // IE11 resets the document referrer on navigation, resulting in an authentication failure.
        // We capture the referrer on page load in a global variable as a workaround. 
        // This code can be removed once we drop support of IE11.
        const embedded = window !== window.top;
        const url = embedded ? BD_DOCUMENT_REFERRER : window.location.href;
        this.store.dispatch(new SetupWidget(url, embedded, widgetConfig));
        // get the current layout
        const layout = this.store.selectSnapshot(ViewState.getLayout);
        // upgrade params to reflect new widget semantics
        const commonConfig = WidgetWiring.convertURIUIConfigToUIConfig(widgetConfig);
        // create a new layout
        const newLayout = WidgetWiring.createLayoutFromConfig(layout, commonConfig);
        // have our new layout take effect
        this.store.dispatch(new ViewActions.SetLayout(newLayout));
        // set default layout after config
        this.store.dispatch(new ViewActions.SetInitialLayout(newLayout));
        // handle widget scroll settings
        const scrollMode = WidgetWiring.getScrollSetting(widgetConfig);
        this.store.dispatch(new SetScrollMode(scrollMode));
        // handle widget loading experience
        const { loaderType, isLoaderActive } = WidgetWiring.getLoader(widgetConfig, this.store.selectSnapshot(ViewState.getLoaderActive), this.store.selectSnapshot(ViewState.getLoaderType));
        // handle widget print setings
        const usePrintImage = WidgetWiring.getUsePrintImage(widgetConfig);
        this.store.dispatch(new ViewActions.SetUsePrintImage(usePrintImage));
        this.store.dispatch([
            new ViewActions.SetLoaderType(loaderType),
            new ViewActions.ToggleLoader(isLoaderActive)
        ]);
    }
    // auth user widget: embedded only
    initWidgetAuth() {
        const embedded = this.store.selectSnapshot(WidgetState.getEmbedded);
        if (embedded) {
            this.store.dispatch(new AuthWidget());
        }
    }
    initViewAnalytics() {
        // init: wait for document ready	
        const loaded = ['loaded', 'interactive', 'complete'].includes(document.readyState);
        const documentReady$ = loaded ? of(loaded) : fromEvent(document, 'DOMContentLoaded');
        documentReady$
            .pipe(take(1), // only initial	
        takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.View));
        });
    }
    // analytic: first user interaction	
    initInteractionAnalytics() {
        // track interaction chance
        this.interactionChance$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.ChanceInteraction));
        });
        // track first interactions 
        this.firstInteraction$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
            this.store.dispatch(new LogWidgetInteraction(WidgetInteractionEnum.FirstInteraction));
        });
    }
    // error handling
    initErrorHandling() {
        // content errors
        this.actions
            .pipe(ofActionDispatched(LoadStoryFail), takeUntil(this.ngUnsubscribe))
            .subscribe(() => this.handleContentErrors());
        // engine errors
        this.engineService.getEvent(EventsEnum.Error)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((e) => this.handleEngineErrors(e));
        // auth errors
        this.actions.pipe(ofActionDispatched(AuthWidgetFailure), takeUntil(this.ngUnsubscribe))
            .subscribe((e) => this.handleAuthErrors(e));
    }
    // sets custom breakpoints
    initWidgetBreakpoints({ 'ui-collapse-label': collapseLabel, 'annotation-collapse': annotationCollapse }) {
        // get label breakpoint
        const labelBreakpoint = [collapseLabel, annotationCollapse].find(value => !isNil(value));
        // validate: 0 allowed
        if (!isNil(labelBreakpoint)) {
            this.store.dispatch(new ViewActions.SetLabelBreakpoint(labelBreakpoint));
        }
    }
    initInteractions() {
        // canvas interaction
        const canvasInteractions$ = this.getCanvasInteraction();
        // app interactions
        const appInteractions$ = this.actions.pipe(ofActionDispatched(SelectNode, ToggleVisibleNodes, HideInverse, TogglePickableNodes, ToggleInversePickableNodes, Isolate));
        // observe: interaction ready
        this.interactionReady$ = this.store.select(StoryChaptersState.getSelectedChapter)
            .pipe(filter((chapter) => chapter && chapter.readyState.model), take(1));
        // observe: interaction chance, engine scrolled into view
        const interactionElement = this.engineService.getCanvas();
        const intersectionThreshold = 0.75;
        this.interactionChance$ = forkJoin(
        // wait for interaction ready
        this.interactionReady$, 
        // wait for scolled into view
        this.getIntersectionObserver(interactionElement, intersectionThreshold))
            .pipe(take(1));
        // observe: first interaction
        this.firstInteraction$ = merge(canvasInteractions$, appInteractions$)
            .pipe(skipUntil(this.interactionReady$), take(1));
    }
    // set initial background
    initBackgroundFromWidget({ background }) {
        if (background) {
            this.setWidgetTransparent(BackgroundModel.isTransparent(background));
            // chapters set: patch all backgrounds
            this.actions
                .pipe(ofActionCompleted(SetChapters), take(1), takeUntil(this.ngUnsubscribe))
                .subscribe(() => {
                this.store.dispatch(new SetAllChaptersBackground(background));
            });
        }
    }
    initBackgroundFromChapter() {
        this.store.select(StoryChaptersState.getSelectedChapter)
            .pipe(filter(chapter => chapter != null), map(chapter => BackgroundModel.isTransparent(chapter.background)), takeUntil(this.ngUnsubscribe))
            .subscribe(isTransparent => {
            this.setWidgetTransparent(isTransparent);
        });
    }
    // set initial camera
    initCamera({ camera }) {
        if (camera) {
            // first chunk loaded: set static camera
            this.actions
                .pipe(ofActionDispatched(LoadStoryChunkSuccess), take(1), takeUntil(this.ngUnsubscribe))
                .subscribe(() => {
                this.store.dispatch(new SetCamera(camera, false));
            });
        }
    }
    // init camera Y rotation
    initRotation({ 'load-rotate': degrees }) {
        if (degrees) {
            // animate camera rotation
            let timeLast = performance.now();
            interval(0, animationFrame)
                .pipe(map(() => degrees * (performance.now() - timeLast) * 0.001), tap(() => timeLast = performance.now()), 
            // end animation on interaction
            takeUntil(this.firstInteraction$), takeUntil(this.ngUnsubscribe))
                .subscribe((deltaY) => {
                this.cameraService.rotateCameraStatic(0, deltaY);
            });
            // return to original camera
            this.firstInteraction$
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe(() => {
                const chapter = this.store.selectSnapshot(StoryChaptersState.getSelectedChapter);
                const chapterCamera = get(chapter, 'camera.location');
                if (chapterCamera) {
                    this.store.dispatch(new SetCamera(chapterCamera, true, 2));
                }
            });
        }
    }
    handleContentErrors() {
        this.store.dispatch(new SetIncomingStory(null, null));
        this.redirectToErrorPage(WidgetErrorTypeEnum.Content);
    }
    handleEngineErrors(error) {
        // extract engine errors
        const engineErrorLog = {
            log: error.log,
            stack: error.stack,
            webglInfo: error.webglInfo
        };
        // prep error message
        const errorMessage = {
            category: error.name,
            log: JSON.stringify(engineErrorLog),
            message: error.message
        };
        // log error
        this.errorService.logError(errorMessage)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe();
        // redirect to error
        this.redirectToErrorPage(WidgetErrorTypeEnum.Engine, error.name);
    }
    handleAuthErrors(event) {
        this.redirectToErrorPage(WidgetErrorTypeEnum.Auth, event.response.errorCode);
    }
    redirectToErrorPage(errorType, errorCode) {
        // don't redirect to /error page if current route is /error
        const url = this.router.url || '';
        if (url.match('/error')) {
            return;
        }
        const basePath = this.locationStrategy.getBaseHref();
        const errorURL = `${basePath}/error`;
        const urlTree = this.router.createUrlTree([errorURL], {
            queryParams: {
                errorType: errorType,
                errorCode: errorCode,
                errorUrl: this.router.url
            }
        });
        const redirectPath = this.router.serializeUrl(urlTree);
        window.location.href = redirectPath;
    }
    getCanvasInteraction() {
        let inputManager;
        return new Observable((observer) => {
            // manage canvas interactions
            const canvas = this.engineService.getCanvas();
            inputManager = new Hammer.Manager(canvas);
            inputManager.add([
                new Hammer.Tap({ event: 'singletap' }),
                new Hammer.Tap({ event: 'doubletap', taps: 2 }),
                // handle mouse down/touch down
                new Hammer.Pan({ event: 'pan', direction: Hammer.DIRECTION_ALL, threshold: 0 })
            ]);
            // !important: capture 'this' context
            const handleCanvasInteraction = () => observer.next();
            const handleHammerEvent = handleCanvasInteraction.bind(this);
            // trigger user interaction
            inputManager.on('panstart', handleHammerEvent);
            inputManager.on('singletap', handleHammerEvent);
        }).pipe(
        // cleanup hammer event handling
        finalize(() => {
            if (inputManager) {
                inputManager.destroy();
                inputManager = null;
            }
        }), takeUntil(this.ngUnsubscribe));
    }
    getIntersectionObserver(targetEl, intersectionThreshold) {
        return new Observable((observer) => {
            // intersection config
            const intersectionCconfig = {
                root: null,
                threshold: intersectionThreshold
            };
            // create intersection observer
            const intersectionObserver = new IntersectionObserver((entries) => {
                // emit observed + cleanup
                const isIntersecting = entries[0].intersectionRatio >= intersectionThreshold;
                if (isIntersecting) {
                    // emit observer + cleanup
                    observer.next(targetEl);
                    observer.complete();
                    intersectionObserver.unobserve(targetEl);
                }
            }, intersectionCconfig);
            // begin intersection observing
            intersectionObserver.observe(targetEl);
        });
    }
    setWidgetTransparent(isTransparent) {
        (isTransparent) ?
            this.renderer.setStyle(document.body, 'background', 'none transparent') :
            this.renderer.removeStyle(document.body, 'background');
    }
}
__decorate([
    Select(WidgetState.getEmbedded),
    __metadata("design:type", Observable)
], WidgetMasterComponent.prototype, "embedded$", void 0);
__decorate([
    Select(WidgetState.getParentURL),
    __metadata("design:type", Observable)
], WidgetMasterComponent.prototype, "parentURL$", void 0);
